import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { LoadingState } from '../../components/common/LoadingState';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { useAuth } from '../../hooks/useAuth';
import { alpha, shellSx, tokens } from '../../theme/tokens';
import { formatDateTime } from '../../utils/format';
import { displayTitle } from '../../utils/labels';
import { useNavigate } from 'react-router-dom';
import { SHOW_NEWS_AGENT, SHOW_NEWS_SOURCES } from '../../config/app';
import { canComposeAlerts, canManageSources, canViewAnalytics } from '../../utils/roles';

export function DashboardTab({ onGoTab }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error } = useAdminDashboard();

  if (error) return <ErrorAlert message={error} />;
  if (loading || !data) return <LoadingState label="Loading dashboard…" />;

  const composeOk = canComposeAlerts(user?.role);
  const analyticsOk = canViewAnalytics(user?.role);
  const scrapeOk = SHOW_NEWS_AGENT && canManageSources(user?.role);
  const pendingLabel =
    user?.role === 'maker' ? 'Sent for review' : 'Waiting for review';

  const metrics = [
    { label: 'Drafts', value: data.articles?.draft ?? 0, tab: 'content', status: 'draft' },
    { label: pendingLabel, value: data.articles?.pending ?? 0, tab: 'content', status: 'pending' },
    {
      label: 'Ready to publish',
      value: data.articles?.approved ?? 0,
      tab: 'content',
      status: 'approved',
    },
    {
      label: user?.role === 'admin' ? 'Live' : 'Your live posts',
      value: data.articles?.published ?? 0,
      tab: 'content',
      status: 'published',
    },
    ...(SHOW_NEWS_SOURCES
      ? [
          {
            label: 'Active news sources',
            value: data.sources?.active ?? 0,
            tab: 'sources',
          },
        ]
      : []),
    ...(composeOk
      ? [
          {
            label: 'Alerts sent',
            value: data.notifications?.total ?? 0,
            tab: 'alerts',
          },
        ]
      : []),
    {
      label:
        analyticsOk || user?.role === 'checker'
          ? 'Story likes'
          : 'Likes on your stories',
      value: data.articles?.likes ?? 0,
      tab: analyticsOk ? 'analytics' : undefined,
    },
  ];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Typography sx={shellSx.textMuted}>
          Signed in as {user?.name}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {scrapeOk && (
            <Button variant="outlined" size="small" onClick={() => onGoTab('intake')}>
              Scrape news →
            </Button>
          )}
          {analyticsOk && (
            <Button variant="outlined" size="small" onClick={() => onGoTab('analytics')}>
              Open Analytics →
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2.5, sm: 3 } }}>
        {metrics.map((metric) => (
          <Grid item xs={6} sm={4} md={3} key={metric.label}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                transition: 'border-color 180ms ease, box-shadow 180ms ease',
                '&:hover': metric.tab
                  ? {
                      borderColor: alpha(tokens.brand, 0.35),
                      boxShadow: `0 8px 22px ${alpha(tokens.ink, 0.06)}`,
                    }
                  : undefined,
              }}
            >
              {metric.tab ? (
                <CardActionArea
                  onClick={() =>
                    onGoTab(metric.tab, { status: metric.status })
                  }
                  sx={{ height: '100%' }}
                >
                  <CardContent
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1.5, sm: 2 },
                      '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        letterSpacing: '-0.03em',
                        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2rem' },
                      }}
                    >
                      {metric.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, mt: 0.5, lineHeight: 1.35 }}
                    >
                      {metric.label}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              ) : (
                <CardContent
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1.5, sm: 2 },
                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      letterSpacing: '-0.03em',
                      fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2rem' },
                    }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, mt: 0.5, lineHeight: 1.35 }}
                  >
                    {metric.label}
                  </Typography>
                </CardContent>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <CardContent
          sx={{
            p: { xs: 1.75, sm: 2.5 },
            '&:last-child': { pb: { xs: 1.75, sm: 2.5 } },
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 1.5, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
          >
            Pending articles
          </Typography>
          {(data.pendingArticles?.length ?? 0) === 0 && (
            <Typography color="text.secondary">None waiting.</Typography>
          )}
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
            {(data.pendingArticles || []).map((a) => (
              <Typography
                component="li"
                key={a.id}
                onClick={() =>
                  navigate(`/admin/content/${a.id}/edit`, {
                    state: { from: '/admin' },
                  })
                }
                sx={{ cursor: 'pointer', '&:hover': { color: tokens.brandInk } }}
              >
                {displayTitle(a.title)}{' '}
                <Box component="span" color="text.secondary">
                  · {a.createdByName || a.sourceName || 'Waiting for review'}
                </Box>
              </Typography>
            ))}
          </Stack>
          <Button onClick={() => onGoTab('content', { status: 'pending' })}>
            Open Content →
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent
          sx={{
            p: { xs: 1.75, sm: 2.5 },
            '&:last-child': { pb: { xs: 1.75, sm: 2.5 } },
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 1.5, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
          >
            Recent alerts
          </Typography>
          {(data.recentNotifications?.length ?? 0) === 0 && (
            <EmptyState description="No recent alerts." />
          )}
          <Stack spacing={1.25} sx={{ mb: 1.5 }}>
            {(data.recentNotifications || []).map((n) => (
              <Box
                key={n.id}
                onClick={() =>
                  composeOk
                    ? onGoTab('alerts')
                    : navigate(`/alerts?nid=${encodeURIComponent(n.id)}`, {
                        state: { from: '/admin' },
                      })
                }
                sx={{ cursor: 'pointer' }}
              >
                <Typography fontWeight={700}>{n.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(n.createdAt)}
                </Typography>
              </Box>
            ))}
          </Stack>
          {composeOk ? (
            <Button onClick={() => onGoTab('alerts')}>Open Compose alerts →</Button>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Updated {formatDateTime(data.generatedAt)}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
